import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { formatPrice, getProductImageUrl } from '../lib/utils';
import { ShoppingCart, Star, Eye } from 'lucide-react';
import { useCart } from '../context/CartContext';
import AddToCartModal from './AddToCartModal';
import type { ProductWithCategory } from '../hooks/useProducts';

interface ProductCardProps {
  product: ProductWithCategory;
}

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const { addItem } = useCart();
  const [showCartModal, setShowCartModal] = React.useState(false);
  
  const handleAddToCart = () => {
    // Convert database product to cart product format
    const cartProduct = {
      id: product.id,
      name: product.name,
      category: product.category,
      subcategory: product.subcategory,
      price: product.price,
      discountPrice: undefined, // No discount_price in current schema
      rating: product.rating,
      image: getProductImageUrl(product.image_url),
      images: product.image_url ? [getProductImageUrl(product.image_url)] : [],
      description: product.description,
      features: product.features,
      specs: product.specifications,
      stock: product.quantity,
      isNew: product.is_new,
      isFeatured: product.is_featured,
      productType: product.product_type
    };
    
    addItem(cartProduct, 1);
    setShowCartModal(true);
  };
  
  const mainImage = getProductImageUrl(product.image_url);
  const currentPrice = product.price; // No discount_price in current schema
  
  return (
    <motion.div 
      className={`product-card group relative bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow ${product.is_featured ? 'ring-2 ring-red-500 ring-opacity-50' : ''}`}
      whileHover={{ y: -5 }}
      transition={{ duration: 0.3 }}
    >
      <div className="relative">
        {/* Fixed-size Image Container with Better Aspect Ratio */}
        <div className="w-full h-[280px] rounded-t-lg overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 relative flex items-center justify-center">
          <img 
            src={mainImage} 
            alt={product.name} 
            className="max-w-full max-h-full object-contain transition-all duration-300 ease-in-out group-hover:scale-105 p-4"
            loading="lazy"
            onError={(e) => {
              // Fallback to a different placeholder if the image fails to load
              e.currentTarget.src = 'https://images.pexels.com/photos/164743/pexels-photo-164743.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&dpr=1';
            }}
          />
          
          {/* Status badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-2 z-10">
            {product.is_new && (
              <span className="bg-green-600 text-white text-xs font-semibold px-2 py-1 rounded-full shadow-sm animate-pulse">
                New
              </span>
            )}
            {product.is_featured && (
              <span className="bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded-full shadow-sm">
                Featured
              </span>
            )}
            {product.product_type !== 'regular' && (
              <span className={`text-white text-xs font-semibold px-2 py-1 rounded-full shadow-sm ${
                product.product_type === 'rental' ? 'bg-blue-600' :
                product.product_type === 'open-box' ? 'bg-green-600' :
                'bg-purple-600'
              }`}>
                {product.product_type === 'rental' ? 'Rental' :
                 product.product_type === 'open-box' ? 'Open Box' :
                 'Factory Returned'}
              </span>
            )}
          </div>

          {/* Stock status */}
          {product.quantity === 0 && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20">
              <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                Out of Stock
              </span>
            </div>
          )}
        </div>
        
        <div className="p-4">
          {/* Brand Name */}
          {product.brand_info && (
            <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide">
              {product.brand_info.name}
            </p>
          )}
          
          {/* Product Name */}
          <h3 className="font-bold text-lg text-black leading-tight hover:text-red-700 transition-colors line-clamp-2 mb-2">
            {product.name}
          </h3>
          
          {/* Category + Subcategory */}
          <p className="text-sm text-gray-500 mb-2 capitalize">
            {product.category_info?.name || product.category} / {product.subcategory_info?.name || product.subcategory.replace(/-/g, ' ')}
          </p>
          
          {/* Description */}
          <p className="text-sm text-gray-600 mb-3 line-clamp-2">
            {product.description}
          </p>
          
          {/* Rating */}
          {product.rating > 0 && (
            <div className="flex items-center mb-3">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i} 
                    className={`h-4 w-4 ${i < Math.floor(product.rating) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                  />
                ))}
              </div>
              <span className="ml-2 text-sm text-gray-600">
                {product.rating.toFixed(1)}
              </span>
              <span className="ml-1 text-xs text-gray-500">
                ({Math.floor(product.rating * 10)} reviews)
              </span>
            </div>
          )}
          
          {/* Price */}
          <div className="mb-4">
            <span className="font-bold text-lg text-black">
              {product.product_type === 'rental' ? `${formatPrice(product.price)}/month` : formatPrice(product.price)}
            </span>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-2">
            <Link 
              to={`/product/${product.id}`}
              className="flex-1 btn btn-outline border-gray-300 text-gray-700 hover:bg-gray-50 py-2 flex items-center justify-center space-x-2 text-sm"
            >
              <Eye className="h-4 w-4" />
              <span>View Details</span>
            </Link>
            
            {product.quantity > 0 && product.product_type === 'regular' && (
              <button 
                onClick={handleAddToCart}
                className="flex-1 btn btn-primary py-2 flex items-center justify-center space-x-2 text-sm"
              >
                <ShoppingCart className="h-4 w-4" />
                <span>Add to Cart</span>
              </button>
            )}
            
            {(product.product_type !== 'regular' || product.quantity === 0) && (
              <Link 
                to="/contact"
                className="flex-1 btn btn-secondary py-2 flex items-center justify-center space-x-2 text-sm"
              >
                <span>
                  {product.quantity === 0 ? 'Notify' :
                   product.product_type === 'rental' ? 'Rent Now' : 'Contact Us'}
                </span>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Add to Cart Modal */}
      <AddToCartModal
        isOpen={showCartModal}
        onClose={() => setShowCartModal(false)}
        product={{
          id: product.id,
          name: product.name,
          price: product.price,
          image: mainImage,
          stock: product.quantity
        }}
      />
    </motion.div>
  );
};

export default ProductCard;